/**
 * Utility functions for MetadataWorkspace.
 * Pure functions for view normalization, column building, action mapping, and table data construction.
 */
import { humanizeEntityName } from '../../utils/entityLabels';

/**
 * What a save says on screen: the Command's own short message when it gives one
 * ("Inventory adjusted successfully"), otherwise the fallback.
 */
export const savedMessage = (result, fallback) => {
  const said = result?.summary || result?.message;
  return typeof said === 'string' && said.length > 0 && said.length <= 50 ? said : fallback;
};

/**
 * Tab title for a screen opened from the menu: "Outbound — Orders", or just
 * "Inventory" when the screen has the workspace's own name (not "Inventory — Inventory").
 */
export const viewTabTitle = (workspaceTitle, viewLabel) => (
  !viewLabel || (workspaceTitle || '').trim().toLowerCase() === viewLabel.trim().toLowerCase()
    ? workspaceTitle
    : `${workspaceTitle} — ${viewLabel}`
);

/**
 * Converts a column/row set (the same shape buildColumns()/loadScript results
 * already use) into CSV text. Escapes per RFC 4180: any value containing a
 * comma, double quote, or newline is wrapped in quotes with internal quotes
 * doubled. Null/undefined become an empty cell, not the literal string "null".
 */
export const rowsToCsv = (columns, rows) => {
  const escapeCell = (value) => {
    if (value === null || value === undefined) return '';
    const str = typeof value === 'object' ? JSON.stringify(value) : String(value);
    if (/[",\n\r]/.test(str)) return `"${str.replace(/"/g, '""')}"`;
    return str;
  };
  const header = columns.map((c) => escapeCell(c.label)).join(',');
  const lines = rows.map((row) => columns.map((c) => escapeCell(row[c.columnId])).join(','));
  return [header, ...lines].join('\r\n');
};

/**
 * Triggers a browser download of text content as a file, via a throwaway
 * Blob + object URL (no server round-trip needed since the CSV is already
 * built client-side from data already fetched for the export).
 */
export const downloadTextFile = (filename, text, mimeType = 'text/csv;charset=utf-8;') => {
  const blob = new Blob([text], { type: mimeType });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

/**
 * Normalize a view definition to the internal master/detail structure.
 *
 * Supports two JSON schemas:
 *   v1 (legacy): { master: {...}, detail: {...} }
 *   v2 (current): { content: [ {id, type, entity, ...}, ... ] }
 */
export const normalizeView = (view) => {
  if (!view) return view;
  if (view.master) return view;
  if (!view.content || !Array.isArray(view.content)) return view;

  const normalized = { ...view };
  const masterBlock = view.content.find((c) => !c.parentContent && (!c.$ref || c.type));
  const detailBlocks = view.content.filter((c) => c !== masterBlock && (c.parentContent || c.$ref));

  if (masterBlock) {
    const master = { ...masterBlock };
    if (master.detail && !detailBlocks.length) {
      normalized.detail = master.detail;
      if (!normalized.detail.parentKey && normalized.detail.parentParam) {
        normalized.detail.parentKey = normalized.detail.parentParam;
        normalized.detail.foreignKey = normalized.detail.parentParam;
      }
    }
    normalized.master = master;
  }

  if (normalized.master?.uiEntity && !normalized.master.entity) {
    normalized.master.entity = normalized.master.uiEntity;
  }

  if (detailBlocks.length > 0) {
    const details = detailBlocks.map((block) => {
      const det = { ...block };
      if (det.uiEntity && !det.entity) det.entity = det.uiEntity;
      if (det.parentParams && typeof det.parentParams === 'object') {
        const entries = Object.entries(det.parentParams);
        if (entries.length === 1) {
          det.foreignKey = entries[0][0];
          det.parentKey = entries[0][1];
        } else if (entries.length > 1) {
          det.foreignKey = entries[0][0];
          det.parentKey = entries[0][1];
          det._parentParamsMap = det.parentParams;
        }
      }
      return det;
    });

    if (details.length === 1) {
      normalized.detail = details[0];
    } else {
      normalized.details = details;
    }
  }

  return normalized;
};

/**
 * Map EntityFieldMetadata.fieldType to TableRenderer column type.
 */
export const mapFieldType = (fieldType) => {
  const mapping = {
    TEXT: 'TEXT',
    NUMBER: 'NUMBER',
    DATE: 'DATE',
    DATETIME: 'DATETIME',
    STATUS: 'STATUS',
    LINK: 'LINK',
    DROPDOWN: 'TEXT',
    TEXTAREA: 'TEXT',
    BOOLEAN: 'BOOLEAN',
    REFERENCE: 'LINK',
    EMAIL: 'TEXT',
    COLOR_PALETTE: 'TEXT',
    // Array-valued fields (e.g. Area.pickedLevels, Area.storageAttribute) render
    // as chips instead of falling through to TEXT, where they'd otherwise show
    // as raw stringified JSON (FH-56, 2026-10-05 -- found while adding grid
    // columns for pickedLevels/selectedReplenishmentAreas).
    MULTISELECT: 'CHIPS',
    MULTICOMBOBOX: 'CHIPS',
    CHECKBOX_GROUP: 'CHIPS',
  };
  return mapping[fieldType] || 'TEXT';
};

/**
 * Map EntityFieldMetadata to a TableRenderer column definition.
 */
export const mapFieldToColumn = (f) => ({
  columnId: f.fieldName,
  label: f.displayLabel || f.fieldName,
  // a field with a status mapping shows as a status chip with its label, even when its form widget is a
  // dropdown (Inventory's status showed its bare code, FH-318)
  type: f.statusMapping ? 'STATUS' : mapFieldType(f.fieldType),
  width: f.tableWidth || 'md',
  sortable: f.tableSortable !== false,
  filterable: f.tableFilterable !== false,
  referenceEntity: f.referenceEntity || undefined,
  statusMapping: f.statusMapping ? (typeof f.statusMapping === 'string' ? JSON.parse(f.statusMapping) : f.statusMapping) : undefined,
  format: f.format,
});

/**
 * A status field whose values come from a list the company keeps (an options script, e.g. its own inventory
 * statuses, FH-318): every value of that list goes into its status mapping, with the list's label, so the grid's
 * chips, the filter chips and the search show the company's own statuses. A value the mapping already has keeps
 * its colour; a new one is gray.
 */
export const mergeStatusOptions = (fields, optionsByField) => (fields || []).map((f) => {
  const options = optionsByField && optionsByField[f.fieldName];
  if (!f.statusMapping || !Array.isArray(options) || options.length === 0) return f;
  const mapping = typeof f.statusMapping === 'string' ? JSON.parse(f.statusMapping) : f.statusMapping;
  const merged = { ...mapping };
  options.forEach((o) => {
    if (!o || o.value === undefined || o.value === null) return;
    const code = String(o.value);
    merged[code] = { ...(mapping[code] || { color: 'gray' }), label: o.label || mapping[code]?.label || code };
  });
  return { ...f, statusMapping: merged };
});

// Custom field values arrive as one map on the row ({ customField: { color: 'Red' } }). Forms, grids
// and details read a field by its name, and custom fields are named "customField.<id>", so each value
// is also put on the row under that name (FH-202).
export const withCustomFieldKeys = (row) => {
  const values = row && row.customField;
  if (!values || typeof values !== 'object' || Array.isArray(values)) return row;
  const flat = { ...row };
  // the WMS keeps every value as text; a Yes/No field's "true"/"false" becomes a real true/false
  Object.keys(values).forEach((k) => {
    const v = values[k];
    flat[`customField.${k}`] = v === 'true' ? true : (v === 'false' ? false : v);
  });
  return flat;
};

/** The filters a view's default chips switch on (its starting view, e.g. Unplanned orders). */
export const defaultChipFilters = (chips) => (chips || [])
  .filter((c) => c.default).map((c) => ({ field: c.field, value: c.value }));

/**
 * A search looks everywhere (FH-203): if the only filters on are the view's default chips, they are
 * lifted, so typing a closed order's number finds it. Filters the user chose themselves stay on.
 * Returns the filters to use and whether the defaults were lifted.
 */
export const liftDefaultChipsForSearch = (activeFilters, chips) => {
  const defaults = defaultChipFilters(chips);
  const same = defaults.length > 0 && defaults.length === activeFilters.length
    && defaults.every((d) => activeFilters.some((a) => a.field === d.field && a.value === d.value));
  return same ? { filters: [], lifted: true } : { filters: activeFilters, lifted: false };
};

/**
 * Build columns from panel config — prefers inline JSON columns[], falls back to entity metadata.
 */
export const buildColumns = (panelConfig, fieldMetaCache) => {
  const declared = buildDeclaredColumns(panelConfig, fieldMetaCache);
  if (!declared) {
    return (fieldMetaCache[panelConfig?.entity] || [])
      .filter((f) => f.isVisibleInTable !== false)
      .sort((a, b) => (a.displayOrder || 0) - (b.displayOrder || 0))
      .map(mapFieldToColumn);
  }
  // A view lists its columns, but fields a customer added (FH-202) can't be in that list:
  // they come after it, for whichever warehouse added them.
  const custom = (fieldMetaCache[panelConfig?.entity] || [])
    .filter((f) => (f.wmsPayloadPath || '').startsWith('customField.') && f.isVisibleInTable !== false)
    .filter((f) => !declared.some((c) => c.columnId === f.fieldName))
    .sort((a, b) => (a.displayOrder || 0) - (b.displayOrder || 0))
    .map(mapFieldToColumn);
  return custom.length > 0 ? [...declared, ...custom] : declared;
};

// The columns a view declares itself (inline, or as a field list); null when it declares none.
const buildDeclaredColumns = (panelConfig, fieldMetaCache) => {
  if (panelConfig?.columns && panelConfig.columns.length > 0) {
    const metaFields = fieldMetaCache[panelConfig?.entity] || [];
    return panelConfig.columns.map((col) => {
      // a status whose values are the company's own list (FH-318): the field's mapping, with that list merged in
      const meta = metaFields.find((m) => m.fieldName === col.field);
      const listed = meta && meta.optionsScript && meta.statusMapping
        ? (typeof meta.statusMapping === 'string' ? JSON.parse(meta.statusMapping) : meta.statusMapping) : null;
      return {
        columnId: col.field,
        label: col.label || col.field,
        type: listed ? 'STATUS' : mapFieldType(col.type || 'TEXT'),
        width: col.width || 'md',
        sortable: col.sortable !== false,
        filterable: col.filterable !== false,
        referenceEntity: col.referenceEntity || undefined,
        statusMapping: listed || col.statusMapping || undefined,
        format: col.format,
        showWhen: col.showWhen || undefined,
        linkAction: col.linkAction || undefined,
      };
    });
  }

  const entity = panelConfig?.entity;
  const fieldsArray = panelConfig?.fields;
  const fields = fieldMetaCache[entity] || [];

  if (fieldsArray && fieldsArray.length > 0) {
    return fieldsArray.map((fieldName) => {
      const f = fields.find((m) => m.fieldName === fieldName);
      if (!f) {
        return { columnId: fieldName, label: fieldName, type: 'TEXT', width: 'md', sortable: false };
      }
      return mapFieldToColumn(f);
    });
  }

  return null;
};

/**
 * Resolve a view's toolbar.advancedSearchFields into full entity_field_metadata
 * -shaped objects ({fieldName, displayLabel, fieldType, searchType, allowedValues,
 * statusMapping, isSearchable}) for AdvancedSearchPanel/StructuredSearchField.
 * Same declared-inline-preferred, fall-back-to-entity-metadata split as buildColumns.
 *
 * Supports two declaration styles in the workspace JSON:
 *   - bare field name strings: ["trailerNumber", "status"] -- resolved entirely
 *     from the entity's cached field metadata.
 *   - inline objects: [{ field, label, type, searchType, options }, ...] -- mapped
 *     to the target shape, falling back to cached metadata (e.g. statusMapping)
 *     for anything not declared inline.
 */
export const resolveAdvancedSearchFieldMeta = (viewToolbar, fieldMetaCache, entity) => {
  const declared = viewToolbar?.advancedSearchFields;
  if (!declared || declared.length === 0) return [];

  const entityFields = fieldMetaCache?.[entity] || [];

  return declared.map((item) => {
    if (typeof item === 'string') {
      const match = entityFields.find((f) => f.fieldName === item);
      return match || { fieldName: item, displayLabel: item, fieldType: 'TEXT', searchType: 'TEXT', isSearchable: true };
    }

    const match = entityFields.find((f) => f.fieldName === item.field);
    return {
      fieldName: item.field,
      displayLabel: item.label || match?.displayLabel || item.field,
      fieldType: item.type || match?.fieldType || 'TEXT',
      searchType: item.searchType || match?.searchType || (item.type === 'RANGE' ? 'RANGE' : item.type === 'ENUM' ? 'ENUM' : 'TEXT'),
      allowedValues: item.options || match?.allowedValues,
      optionsScript: item.optionsScript || match?.optionsScript,
      statusMapping: match?.statusMapping,
      isSearchable: item.isSearchable !== undefined ? item.isSearchable : (match?.isSearchable !== undefined ? match.isSearchable : true),
    };
  });
};

/**
 * The fields Smart Search (the search box) offers: the view's declared search fields, the same ones Advanced
 * Search shows; else the grid's own columns (what the user sees in the rows); else the entity's fields.
 */
export const resolveSmartSearchFieldMeta = (declaredMeta, block, fieldMetaCache) => {
  if (declaredMeta && declaredMeta.length > 0) return declaredMeta;
  const entityFields = fieldMetaCache?.[block?.entity] || [];
  if (Array.isArray(block?.columns) && block.columns.length > 0) {
    return block.columns
      .filter((c) => c && c.field && !c.hidden)
      .map((c) => {
        const match = entityFields.find((f) => f.fieldName === c.field) || {};
        return {
          ...match,
          fieldName: c.field,
          displayLabel: c.label || match.displayLabel || c.field,
          fieldType: c.type || match.fieldType || 'TEXT',
        };
      });
  }
  return entityFields;
};

/**
 * Evaluate a row action's showWhen against a row's data. Supports the
 * original single-condition object ({field, in, notIn}) and, for cases that
 * need to gate on more than one field at once (e.g. a fast-path action that
 * should only appear for a specific workType AND a specific status), an
 * array of condition objects that must ALL match (AND).
 */
const matchesShowWhenCondition = (item, condition) => {
  if (!condition?.field) return true;
  const rowValue = String(item[condition.field] ?? '');
  if (condition.in && Array.isArray(condition.in)) return condition.in.map(String).includes(rowValue);
  if (condition.notIn && Array.isArray(condition.notIn)) return !condition.notIn.map(String).includes(rowValue);
  return true;
};

export const matchesShowWhen = (item, showWhen) => {
  if (!showWhen) return true;
  if (Array.isArray(showWhen)) return showWhen.every((c) => matchesShowWhenCondition(item, c));
  return matchesShowWhenCondition(item, showWhen);
};

/**
 * Role-gates an action independent of row data -- e.g. a platform-admin-only
 * action like Reset Tenant that should never even appear for a regular user,
 * not just be blocked server-side after the fact. `requiresRole` on the
 * action JSON is a list of role strings; the action is visible if the
 * current user has ANY of them. No requiresRole means no gate (visible to
 * everyone, same as today). Backend enforcement (the entity function /
 * script itself) remains the real security boundary -- this only controls
 * whether the button is offered in the first place.
 */
export const matchesRequiresRole = (requiresRole, currentUserRoles) => {
  if (!requiresRole || requiresRole.length === 0) return true;
  if (!Array.isArray(currentUserRoles) || currentUserRoles.length === 0) return false;
  return requiresRole.some((r) => currentUserRoles.includes(r));
};

/**
 * Map a JSON action definition to internal action format.
 */
export const mapAction = (a, i, prefix, selectionMode) => ({
  // an explicit actionId lets two actions share a Command (e.g. Void for shipped vs staged shipments, FH-141)
  actionId: a.actionId || a.actionScript || a.script || `${prefix}-${i}`,
  label: a.label,
  icon: a.icon,
  color: a.color || null,
  type: a.type || 'EXECUTE',
  actionType: a.type || 'EXECUTE',
  scriptName: a.actionScript || a.script || null,  // support both keys for backward compat
  loadScript: a.loadScript || null,
  selectionMode,
  showWhen: a.showWhen || null,
  confirmMessage: a.confirmMessage || a.confirm || null,
  pickerConfig: a.pickerConfig || null,
  criteriaBuilder: a.criteriaBuilder || null,
  countEntry: a.countEntry || null,
  formEntity: a.formEntity || a.wizardEntity || null,
  formDetailEntity: a.formDetailEntity || a.wizardDetailEntity || null,
  requiresRole: a.requiresRole || null,
  prefill: a.prefill || null,
  forceCreate: !!a.forceCreate,
  rapidEntry: a.rapidEntry || null,
  fieldOverrides: a.fieldOverrides || null,
  targetWorkspace: a.targetWorkspace || null,
  targetView: a.targetView || null,
  contextParams: a.contextParams || null,
  confirmGrid: a.confirmGrid || null,
  // CUSTOM_FIELD actions: "add" or "edit" (FH-205)
  mode: a.mode || null,
  // the action adds or removes an entity's fields: open grids and forms re-read them (FH-205)
  entityFieldsChanged: !!a.entityFieldsChanged,
  pdfUrl: a.pdfUrl || null,
  // PHOTOS actions: which record the row's photos belong to
  photos: a.photos || null,
  // WIZARD_RUN actions: the inspection or work order to start, or the run to open (FH-366)
  wizardRun: a.wizardRun || null,
  // ASK_ATOM actions: the sentence handed to Atom, {field} filled from the row (FH-419)
  atom: a.atom || null,
  minSelection: selectionMode === 'ONE_OR_MORE' ? 1 : undefined,
  // a header button that opens a menu of actions (FH-242)
  items: Array.isArray(a.items) ? a.items.map((item, i) => mapAction(item, i, `${prefix}-item`, 'NONE')) : null,
  // a FLEX Command run first: {ok: false, message} stops the action and says why (FH-242)
  precheckScript: a.precheckScript || null,
});

/**
 * Build actions from JSON — merges view-level + grid-level actions, then
 * drops any action the current user's roles don't satisfy (requiresRole).
 * Role-gating here is purely visibility -- the authoritative check still
 * lives server-side in the action's own script/entity function.
 */
export const buildJsonActions = (panelConfig, viewActions, currentUserRoles) => {
  const gridActions = panelConfig?.actions || {};
  const mergedHeader = gridActions.header || viewActions.header || [];
  const mergedRow = gridActions.row || viewActions.row || [];
  const mergedBulk = gridActions.bulk || viewActions.bulk || [];

  const byRole = (a) => matchesRequiresRole(a.requiresRole, currentUserRoles);

  return {
    globalActions: mergedHeader.map((a, i) => mapAction(a, i, 'header', 'NONE')).filter(byRole),
    rowActions: mergedRow.map((a, i) => mapAction(a, i, 'row', 'SINGLE')).filter(byRole),
    bulkActions: mergedBulk.map((a, i) => mapAction(a, i, 'bulk', 'ONE_OR_MORE')).filter(byRole),
  };
};

/**
 * Build table data for TableRenderer.
 */
export const buildTableData = (panelConfig, data, title, activeViewId, expandedRowId, fieldMetaCache, viewActions, activeFilters, currentUserRoles) => {
  let columns = buildColumns(panelConfig, fieldMetaCache);

  // Filter columns by showWhen when chip filters are active
  if (activeFilters && activeFilters.length > 0) {
    columns = columns.filter(col => {
      if (!col.showWhen) return true;
      const { field, in: inValues } = col.showWhen;
      if (!field || !inValues) return true;
      // Check if any active filter matches the showWhen field
      const activeForField = activeFilters.filter(f => f.field === field);
      if (activeForField.length === 0) return true; // no filter on this field — show column
      // Show column if any active filter value is in the showWhen.in list
      return activeForField.some(f => inValues.includes(f.value));
    });
  }
  const { rowActions, bulkActions, globalActions } = buildJsonActions(panelConfig, viewActions, currentUserRoles);
  const pkName = panelConfig?.primaryKey || 'id';
  const entityId = panelConfig?.entity || panelConfig?.loadScript || activeViewId;

  const rows = data.map((item, idx) => {
    const visibleActions = rowActions.filter((a) => matchesShowWhen(item, a.showWhen));

    const rowId = String(item[pkName] || item.id || idx);
    return {
      rowId,
      data: item,
      actions: visibleActions.map((a) => a.actionId),
      selectable: true,
      highlighted: expandedRowId === rowId,
      highlightColor: expandedRowId === rowId ? 'success' : null,
    };
  });

  return {
    tableId: `ws-${entityId}-${activeViewId}`,
    title: title || humanizeEntityName(entityId),
    entityType: entityId,
    columns,
    rows,
    rowActions,
    bulkActions,
    globalActions,
    selectable: bulkActions.length > 0,
    pageSize: panelConfig?.pageSize || null,
    // what one row is called in the count line ("restock"), when the entity name isn't a word users say
    rowNoun: panelConfig?.rowNoun || null,
  };
};

/**
 * Build detail table data — supports row actions defined on the detail content block.
 */
export const buildDetailTableData = (panelConfig, data, title, activeViewId, fieldMetaCache, currentUserRoles) => {
  const columns = buildColumns(panelConfig, fieldMetaCache);
  const pkName = panelConfig?.primaryKey || 'id';
  const entityId = panelConfig?.entity || panelConfig?.loadScript || activeViewId;

  const byRole = (a) => matchesRequiresRole(a.requiresRole, currentUserRoles);
  const detailActions = panelConfig?.actions?.row || [];
  const rowActions = detailActions.map((a, i) => mapAction(a, i, 'detail-row', 'SINGLE')).filter(byRole);
  const detailBulkActions = panelConfig?.actions?.bulk || [];
  const bulkActions = detailBulkActions.map((a, i) => mapAction(a, i, 'detail-bulk', 'ONE_OR_MORE')).filter(byRole);
  const detailHeaderActions = panelConfig?.actions?.header || [];
  const headerActions = detailHeaderActions.map((a, i) => mapAction(a, i, 'detail-header', 'NONE')).filter(byRole);
  const isSelectable = panelConfig?.selectable === true || bulkActions.length > 0;

  const rows = data.map((item, idx) => {
    const visibleActions = rowActions.filter((a) => matchesShowWhen(item, a.showWhen));

    return {
      rowId: String(item[pkName] || item.id || idx),
      data: item,
      actions: visibleActions.map((a) => a.actionId),
      selectable: isSelectable,
    };
  });

  return {
    tableId: `ws-detail-${entityId}-${activeViewId}`,
    title: title || humanizeEntityName(entityId),
    entityType: entityId,
    columns,
    rows,
    rowActions,
    bulkActions,
    globalActions: headerActions,
    selectable: isSelectable,
    rowNoun: panelConfig?.rowNoun || null,
    groupBy: panelConfig?.groupBy || null,
    // Field summed into the group-header "<label>: N" chip (TableRenderer). Defaults
    // to 'quantity' for back-compat, but most grouped grids don't have a literal
    // 'quantity' field (e.g. PickWork has pickQuantity/completedQuantity) -- that
    // silently summed to 0 every time rather than surfacing anything meaningful.
    groupQuantityField: panelConfig?.groupQuantityField || 'quantity',
    // Chip label prefix -- defaults to "Qty" for back-compat. Set explicitly
    // (e.g. "Picked Qty") whenever groupQuantityField isn't the generic/obvious
    // "quantity", so the chip doesn't read as ambiguous (was reported as
    // confusing on the Pick Work grid: "1 item Qty: 0" for a not-yet-picked row,
    // where 0 is correct for completedQuantity but the label didn't say so).
    groupQuantityLabel: panelConfig?.groupQuantityLabel || 'Qty',
  };
};
